import { createSlice } from "@reduxjs/toolkit";
import {
  getAllMileageDeviationsThunk,
  updateMileageDeviationStatusThunk,
  updateMileageDeviationStatusOnlyThunk,
  updateMileageDeviationCommentOnlyThunk,
  updateMileageDeviationCommentThunk
} from "./mileageDeviationsAction";
import { store } from "app/store";

export interface IMileageDeviationState {
  loading: boolean;
  mileageDeviations: Array<any>;
  totalMileageDeviations: number | null;
  updatingStatus: boolean;
  updatingComment: boolean;
}

const initialState: IMileageDeviationState = {
  loading: false,
  mileageDeviations: [],
  totalMileageDeviations: null,
  updatingStatus: false,
  updatingComment: false
};

const mileageDeviationsSlice = createSlice({
  name: "mileageDeviations",
  initialState,
  reducers: {
    resetMileageDeviations: (state) => {
      state.mileageDeviations = [];
    }
  },
  extraReducers: (builder) => {
    // Get mileage deviations cases
    builder.addCase(getAllMileageDeviationsThunk.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getAllMileageDeviationsThunk.fulfilled, (state, action) => {
      state.mileageDeviations = action.payload?.results || [];
      state.totalMileageDeviations = action.payload?.rowCount || 0;
      state.loading = false;
    });

    builder.addCase(getAllMileageDeviationsThunk.rejected, (state) => {
      state.loading = false;
    });

    // Update status cases
    builder.addCase(updateMileageDeviationStatusThunk.pending, (state) => {
      state.updatingStatus = true;
    });

    builder.addCase(
      updateMileageDeviationStatusThunk.fulfilled,
      (state, action) => {
        state.updatingStatus = false;

        if (action.payload) {
          const { deviationId, status, comment, userEmail } = action.payload;
          const deviationIndex = state.mileageDeviations.findIndex(
            (deviation) => deviation.uniqueId === deviationId
          );

          if (deviationIndex !== -1) {
            const statusMap: { [key: number]: string } = {
              0: "New",
              1: "Resolved",
              2: "Denied"
            };

            state.mileageDeviations[deviationIndex].status =
              statusMap[status] || "Unknown";
            state.mileageDeviations[deviationIndex].comment = comment;
            state.mileageDeviations[deviationIndex].commentedOn =
              new Date().toISOString();

            if (userEmail) {
              state.mileageDeviations[deviationIndex].commenterUserName =
                userEmail;
            } else {
              state.mileageDeviations[deviationIndex].commenterUserName =
                "Current User";
            }

            state.mileageDeviations = [...state.mileageDeviations];
          }
        }
      }
    );

    builder.addCase(updateMileageDeviationStatusThunk.rejected, (state) => {
      state.updatingStatus = false;
    });

    // Update status only cases
    builder.addCase(updateMileageDeviationStatusOnlyThunk.pending, (state) => {
      state.updatingStatus = true;
    });

    builder.addCase(
      updateMileageDeviationStatusOnlyThunk.fulfilled,
      (state, action) => {
        state.updatingStatus = false;

        if (action.payload) {
          const { deviationId, status, userEmail } = action.payload;
          const deviationIndex = state.mileageDeviations.findIndex(
            (deviation) => deviation.uniqueId === deviationId
          );

          if (deviationIndex !== -1) {
            const statusMap: { [key: number]: string } = {
              0: "New",
              1: "Resolved",
              2: "Denied"
            };

            state.mileageDeviations[deviationIndex].status =
              statusMap[status] || "Unknown";

            if (userEmail) {
              state.mileageDeviations[deviationIndex].commenterUserName =
                userEmail;
            }

            state.mileageDeviations = [...state.mileageDeviations];
          }
        }
      }
    );

    builder.addCase(updateMileageDeviationStatusOnlyThunk.rejected, (state) => {
      state.updatingStatus = false;
    });

    // Update comment only cases
    builder.addCase(updateMileageDeviationCommentOnlyThunk.pending, (state) => {
      state.updatingComment = true;
    });

    builder.addCase(
      updateMileageDeviationCommentOnlyThunk.fulfilled,
      (state, action) => {
        state.updatingComment = false;

        if (action.payload) {
          const { deviationId, comment, userEmail } = action.payload;
          const deviationIndex = state.mileageDeviations.findIndex(
            (deviation) => deviation.uniqueId === deviationId
          );

          if (deviationIndex !== -1) {
            state.mileageDeviations[deviationIndex].comment = comment;
            state.mileageDeviations[deviationIndex].commentedOn =
              new Date().toISOString();

            if (userEmail) {
              state.mileageDeviations[deviationIndex].commenterUserName =
                userEmail;
            } else {
              state.mileageDeviations[deviationIndex].commenterUserName =
                "Current User";
            }

            state.mileageDeviations = [...state.mileageDeviations];
          }
        }
      }
    );

    builder.addCase(
      updateMileageDeviationCommentOnlyThunk.rejected,
      (state) => {
        state.updatingComment = false;
      }
    );

    // Update comment cases (legacy - with status)
    builder.addCase(updateMileageDeviationCommentThunk.pending, (state) => {
      state.updatingComment = true;
    });

    builder.addCase(
      updateMileageDeviationCommentThunk.fulfilled,
      (state, action) => {
        state.updatingComment = false;

        if (action.payload) {
          const { deviationId, comment, userEmail } = action.payload;
          const deviationIndex = state.mileageDeviations.findIndex(
            (deviation) => deviation.uniqueId === deviationId
          );

          if (deviationIndex !== -1) {
            state.mileageDeviations[deviationIndex].comment = comment;
            state.mileageDeviations[deviationIndex].commentedOn =
              new Date().toISOString();

            if (userEmail) {
              state.mileageDeviations[deviationIndex].commenterUserName =
                userEmail;
            } else {
              state.mileageDeviations[deviationIndex].commenterUserName =
                "Current User";
            }

            state.mileageDeviations = [...state.mileageDeviations];
          }
        }
      }
    );

    builder.addCase(updateMileageDeviationCommentThunk.rejected, (state) => {
      state.updatingComment = false;
    });
  }
});

export * from "./mileageDeviationsAction";
export const { resetMileageDeviations } = mileageDeviationsSlice.actions;

export default mileageDeviationsSlice.reducer;
